import { createFileRoute, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { AppShell } from "@/components/app-shell";
import { requireRole } from "@/lib/session";
import { useLearnerContext } from "@/lib/learner-context";
import { supabase } from "@/integrations/supabase/client";
import { Play, CheckCircle2, Video, FileText, Headphones, Link2, File, Loader2, ExternalLink } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { offlineDB } from "@/lib/offline/db";
import { getOfflineBlobUrl } from "@/lib/offline/downloads";
import { DownloadButton } from "@/components/offline/download-button";

export const Route = createFileRoute("/learner/lessons")({
  ssr: false,
  beforeLoad: () => requireRole("learner"),
  head: () => ({ meta: [{ title: "Lessons — MAKAI" }] }),
  validateSearch: (s: Record<string, unknown>) => ({ subject: typeof s.subject === "string" ? s.subject : undefined }),
  component: LessonsPage,
});

type Lesson = {
  id: string;
  title: string;
  topic: string | null;
  description: string | null;
  content_type: "video" | "pdf" | "ppt" | "audio" | "text" | "link";
  content_url: string | null;
  content_text: string | null;
  duration_min: number | null;
  is_downloadable: boolean;
  subject: { id: string; name: string } | null;
};

type Completion = { lesson_id: string; status: string; progress_percent: number };

const ICONS = { video: Video, pdf: FileText, ppt: FileText, audio: Headphones, text: File, link: Link2 } as const;

function LessonsPage() {
  const { ctx, loading } = useLearnerContext();
  const { subject } = useSearch({ from: "/learner/lessons" });
  const [subjects, setSubjects] = useState<{ id: string; name: string }[]>([]);
  const [filter, setFilter] = useState<string>(subject ?? "all");
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [comps, setComps] = useState<Record<string, Completion>>({});
  const [active, setActive] = useState<Lesson | null>(null);

  useEffect(() => {
    if (!ctx) return;
    (async () => {
      const db = offlineDB();
      const offline = typeof navigator !== "undefined" && !navigator.onLine;

      // Offline-first: hydrate from Dexie cache immediately if present.
      if (db) {
        const cached = await db.rows.where("table").equals("lessons").toArray();
        if (cached.length) {
          setLessons(cached.map((c) => c.data as Lesson));
        }
        const cachedSubs = await db.kv.get("learner.subjects");
        if (cachedSubs?.value) setSubjects(cachedSubs.value as { id: string; name: string }[]);
      }

      if (offline) return;

      let subs: { id: string; name: string }[] = [];
      if (ctx.classId) {
        const { data } = await supabase.from("class_subjects")
          .select("subject:subjects_catalog(id, name)").eq("class_id", ctx.classId);
        subs = ((data ?? []) as { subject: { id: string; name: string } | null }[])
          .map((r) => r.subject!).filter(Boolean);
      }
      setSubjects(subs);
      if (db) await db.kv.put({ key: "learner.subjects", value: subs, updated_at: Date.now() });

      let q = supabase.from("lessons")
        .select("id, title, topic, description, content_type, content_url, content_text, duration_min, is_downloadable, subject:subjects_catalog(id, name)")
        .eq("school_id", ctx.schoolId).eq("published", true).order("sort_order");
      if (filter !== "all") q = q.eq("subject_id", filter);
      const { data: ls } = await q;
      const fresh = (ls as Lesson[] | null) ?? [];
      setLessons(fresh);
      if (db && fresh.length) {
        await db.rows.bulkPut(fresh.map((l) => ({
          id: l.id, table: "lessons", data: l, updated_at: new Date().toISOString(), fetched_at: Date.now(),
        })));
      }

      const { data: cs } = await supabase.from("lesson_completions")
        .select("lesson_id, status, progress_percent").eq("learner_id", ctx.learnerId);
      const map: Record<string, Completion> = {};
      ((cs ?? []) as Completion[]).forEach((c) => { map[c.lesson_id] = c; });
      setComps(map);
    })();
  }, [ctx, filter]);

  async function open(l: Lesson) {
    setActive(l);
    if (!ctx) return;
    await supabase.from("lesson_completions").upsert({
      learner_id: ctx.learnerId, lesson_id: l.id, status: "in_progress",
      progress_percent: Math.max(comps[l.id]?.progress_percent ?? 0, 25), last_accessed_at: new Date().toISOString(),
    }, { onConflict: "learner_id,lesson_id" });
    setComps((m) => ({ ...m, [l.id]: { lesson_id: l.id, status: "in_progress", progress_percent: Math.max(m[l.id]?.progress_percent ?? 0, 25) } }));
  }

  async function markComplete(l: Lesson) {
    if (!ctx) return;
    await supabase.from("lesson_completions").upsert({
      learner_id: ctx.learnerId, lesson_id: l.id, status: "completed",
      progress_percent: 100, completed_at: new Date().toISOString(), last_accessed_at: new Date().toISOString(),
    }, { onConflict: "learner_id,lesson_id" });
    // award XP
    const { data: xp } = await supabase.from("learner_xp").select("*").eq("learner_id", ctx.learnerId).maybeSingle();
    const today = new Date().toISOString().slice(0, 10);
    const isNewDay = !xp || xp.last_active_date !== today;
    const yesterday = new Date(Date.now() - 86400_000).toISOString().slice(0, 10);
    const newStreak = !xp ? 1 : xp.last_active_date === yesterday ? xp.current_streak + 1 : isNewDay ? 1 : xp.current_streak;
    await supabase.from("learner_xp").upsert({
      learner_id: ctx.learnerId,
      points: (xp?.points ?? 0) + 10,
      current_streak: newStreak,
      longest_streak: Math.max(xp?.longest_streak ?? 0, newStreak),
      last_active_date: today,
    });
    setComps((m) => ({ ...m, [l.id]: { lesson_id: l.id, status: "completed", progress_percent: 100 } }));
    toast.success("Lesson complete! +10 XP 🎉");
  }

  if (loading) return <AppShell role="learner"><div className="grid place-items-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div></AppShell>;

  return (
    <AppShell role="learner" title="Lessons" subtitle="Watch, read, listen — at your own pace.">
      <div className="-mx-1 mb-5 flex gap-2 overflow-x-auto px-1 pb-1">
        <Chip label="All" active={filter === "all"} onClick={() => setFilter("all")} />
        {subjects.map((s) => <Chip key={s.id} label={s.name} active={filter === s.id} onClick={() => setFilter(s.id)} />)}
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {lessons.length === 0 && (
          <div className="col-span-full rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
            No lessons yet for this filter.
          </div>
        )}
        {lessons.map((l) => {
          const c = comps[l.id];
          const done = c?.status === "completed";
          const pct = c?.progress_percent ?? 0;
          const Icon = ICONS[l.content_type] ?? File;
          return (
            <div key={l.id} className="flex flex-col rounded-2xl border border-border bg-card p-5 shadow-soft">
              <div className="flex items-center justify-between">
                <span className="inline-flex items-center gap-1 rounded-md bg-primary-soft px-2 py-0.5 text-[11px] font-semibold text-primary"><Icon className="h-3 w-3" /> {l.content_type.toUpperCase()}</span>
                <span className="text-[11px] text-muted-foreground">{l.duration_min ?? 10} min</span>
              </div>
              <h3 className="mt-3 font-display text-base font-bold line-clamp-2">{l.title}</h3>
              <p className="mt-1 text-xs text-muted-foreground">{l.subject?.name ?? ""}{l.topic ? ` · ${l.topic}` : ""}</p>
              <div className="mt-4 h-1.5 overflow-hidden rounded-full bg-muted">
                <div className="h-full rounded-full bg-primary" style={{ width: `${pct}%` }} />
              </div>
              <div className="mt-4 flex items-center justify-between">
                <span className="text-xs text-muted-foreground">{pct}% complete</span>
                <button onClick={() => open(l)} className={cn("inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold", done ? "bg-primary-soft text-primary" : "bg-primary text-primary-foreground")}>
                  {done ? <><CheckCircle2 className="h-3.5 w-3.5" /> Done</> : <><Play className="h-3.5 w-3.5" /> {pct > 0 ? "Resume" : "Start"}</>}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {active && <LessonModal lesson={active} done={comps[active.id]?.status === "completed"} onClose={() => setActive(null)} onComplete={() => { markComplete(active); setActive(null); }} />}
    </AppShell>
  );
}

function Chip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button onClick={onClick} className={cn(
      "shrink-0 rounded-full border px-3.5 py-1.5 text-xs font-semibold transition",
      active ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-muted-foreground hover:text-foreground",
    )}>{label}</button>
  );
}

function LessonModal({ lesson, done, onClose, onComplete }: { lesson: Lesson; done: boolean; onClose: () => void; onComplete: () => void }) {
  const [playUrl, setPlayUrl] = useState<string | null>(lesson.content_url);
  const [isOffline, setIsOffline] = useState(false);

  useEffect(() => {
    let revoke: string | null = null;
    (async () => {
      const blob = await getOfflineBlobUrl(lesson.id);
      if (blob) {
        setPlayUrl(blob);
        setIsOffline(true);
        revoke = blob;
      } else {
        setPlayUrl(lesson.content_url);
        setIsOffline(false);
      }
    })();
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [lesson.id, lesson.content_url]);

  const kind: "video" | "pdf" | "audio" | "resource" =
    lesson.content_type === "video" ? "video"
    : lesson.content_type === "audio" ? "video"
    : lesson.content_type === "pdf" || lesson.content_type === "ppt" ? "pdf"
    : "resource";

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-foreground/40 p-0 sm:items-center sm:p-4" onClick={onClose}>
      <div className="w-full max-w-3xl rounded-t-2xl bg-card p-5 shadow-card sm:rounded-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-primary">
              {lesson.subject?.name} · {lesson.content_type}
              {isOffline && <span className="ml-2 rounded-md bg-emerald-100 px-1.5 py-0.5 text-[10px] text-emerald-700">Offline copy</span>}
            </div>
            <h3 className="mt-1 font-display text-lg font-bold">{lesson.title}</h3>
            {lesson.topic && <p className="text-xs text-muted-foreground">{lesson.topic}</p>}
          </div>
          <button onClick={onClose} className="rounded-lg px-2 py-1 text-sm text-muted-foreground hover:bg-muted">Close</button>
        </div>

        <div className="mt-4 max-h-[60vh] overflow-y-auto rounded-xl border border-border bg-muted/30 p-4">
          {lesson.content_type === "video" && playUrl && (
            <video src={playUrl} controls className="aspect-video w-full rounded-lg bg-black" />
          )}
          {lesson.content_type === "audio" && playUrl && (
            <audio src={playUrl} controls className="w-full" />
          )}
          {(lesson.content_type === "pdf" || lesson.content_type === "ppt") && playUrl && (
            <iframe src={playUrl} className="h-[55vh] w-full rounded-lg bg-white" title={lesson.title} />
          )}
          {lesson.content_type === "link" && lesson.content_url && (
            <a href={lesson.content_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline">
              Open external resource <ExternalLink className="h-4 w-4" />
            </a>
          )}
          {lesson.content_type === "text" && (
            <div className="prose prose-sm max-w-none whitespace-pre-wrap text-sm">{lesson.content_text ?? lesson.description ?? "No content yet."}</div>
          )}
          {lesson.description && lesson.content_type !== "text" && (
            <p className="mt-3 text-sm text-muted-foreground">{lesson.description}</p>
          )}
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
          {lesson.is_downloadable && lesson.content_url && (
            <DownloadButton
              id={lesson.id}
              kind={kind}
              title={lesson.title}
              url={lesson.content_url}
              compact
            />
          )}
          <button onClick={onComplete} disabled={done} className={cn("ml-auto inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold", done ? "bg-primary-soft text-primary" : "bg-primary text-primary-foreground hover:bg-primary/90")}>
            <CheckCircle2 className="h-4 w-4" /> {done ? "Completed" : "Mark complete (+10 XP)"}
          </button>
        </div>
      </div>
    </div>
  );
}
